/** Рейтинг, статистика и награды.
 *
 * Три вкладки, а не три экрана: игрок приходит сюда за одним вопросом —
 * «как у меня дела», — и ответ на него собирается из всех трёх.
 *
 * Своя строка в таблице вынесена из общего блока, залита акцентом и
 * прилипает к нижнему краю: её находят периферийным зрением, не читая
 * чужие имена.
 */

import { useEffect, useState } from 'react'

import { fetchAchievements, fetchLeaderboard, fetchStats } from '../api/endpoints'
import type {
  AchievementItem,
  BoardScope,
  Difficulty,
  LeaderboardResponse,
  PlayerStats,
} from '../api/types'
import { AvatarFrame } from '../components/AvatarFrame'
import { Crystal, DIFFICULTIES, ProgressBar, Rank } from '../components/ui'
import { formatGap, formatNumber, formatTime } from '../game/format'
import { pluralOf, t } from '../i18n'
import { backButton } from '../telegram/webapp'

type Tab = 'board' | 'stats' | 'awards'

const SCOPES: BoardScope[] = ['total', 'weekly', 'daily', 'best_time']

export interface RatingScreenProps {
  playerName: string
  onExit: () => void
  onOpenDaily: () => void
}

export function RatingScreen({ playerName, onExit, onOpenDaily }: RatingScreenProps) {
  const [tab, setTab] = useState<Tab>('board')

  useEffect(() => backButton.show(onExit), [onExit])

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('rating.title')}</span>
        <span className="topbar__spacer" />
      </header>

      <div className="screen__body">
        <div className="segmented">
          {(['board', 'stats', 'awards'] as Tab[]).map((item) => (
            <button
              key={item}
              type="button"
              className={`segmented__item ${tab === item ? 'segmented__item--active' : ''}`.trim()}
              onClick={() => setTab(item)}
            >
              {t(`rating.tab.${item}`)}
            </button>
          ))}
        </div>

        {tab === 'board' && <BoardTab playerName={playerName} onOpenDaily={onOpenDaily} />}
        {tab === 'stats' && <StatsTab />}
        {tab === 'awards' && <AwardsTab />}
      </div>
    </div>
  )
}

function BoardTab({
  playerName,
  onOpenDaily,
}: {
  playerName: string
  onOpenDaily: () => void
}) {
  const [scope, setScope] = useState<BoardScope>('total')
  const [difficulty, setDifficulty] = useState<Difficulty>('hard')
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    void fetchLeaderboard(scope, scope === 'best_time' ? difficulty : undefined)
      .then((response) => alive && setData(response))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [scope, difficulty])

  // Время меньше — лучше, очки больше — лучше. Одно и то же число в разных
  // таблицах значит разное, и форматируется оно тоже по-разному.
  const isTime = scope === 'best_time' || scope === 'daily'
  const format = (value: number) => (isTime ? formatTime(value) : formatNumber(value))

  return (
    <>
      <div className="pill-row">
        {SCOPES.map((item) => (
          <button
            key={item}
            type="button"
            className={`pill ${scope === item ? 'pill--active' : ''}`.trim()}
            onClick={() => setScope(item)}
          >
            {t(`rating.scope.${item}`)}
          </button>
        ))}
      </div>

      {scope === 'best_time' && (
        <div className="button-row">
          {DIFFICULTIES.map((level) => (
            <button
              key={level}
              type="button"
              className="segmented__item"
              style={{
                height: 38,
                borderRadius: 10,
                background: difficulty === level ? 'var(--bg)' : 'var(--bg-secondary)',
                boxShadow:
                  difficulty === level ? `inset 0 0 0 1.5px var(--level-${DIFFICULTIES.indexOf(level) + 1})` : 'none',
                color: difficulty === level ? 'var(--text)' : 'var(--text-hint)',
                fontWeight: difficulty === level ? 600 : 400,
              }}
              onClick={() => setDifficulty(level)}
            >
              {t(`level.${level}`)}
            </button>
          ))}
        </div>
      )}

      {scope === 'best_time' && data && data.total_players > 0 && (
        <span className="section-label">
          {t('rating.bestTimeHeader', {
            level: t(`level.${difficulty}`),
            count: formatNumber(data.total_players),
            word: pluralOf('plural.players', data.total_players),
          })}
        </span>
      )}

      {loading && <span className="hint-text">{t('app.loading')}</span>}

      {!loading && data && data.rows.length === 0 && (
        <div className="empty-state">
          <span className="empty-state__icon">◆</span>
          <span style={{ fontSize: 'var(--size-row)', fontWeight: 600 }}>
            {scope === 'daily' ? t('rating.emptyDaily') : t('rating.empty')}
          </span>
          <span className="hint-text">
            {scope === 'daily' ? t('rating.emptyDailyHint') : t('rating.emptyHint')}
          </span>
          {scope === 'daily' && (
            <button
              type="button"
              className="button button--primary button--compact"
              onClick={onOpenDaily}
            >
              {t('rating.openDaily')}
            </button>
          )}
        </div>
      )}

      {!loading && data && data.rows.length > 0 && (
        <>
          <div className="list">
            {data.rows.map((row) => (
              <div className="list__row" key={row.user_id}>
                <Rank rank={row.rank} />
                {/* Аватар с рамкой: рамку покупают ради того, чтобы её
                    видели другие, и таблица — единственное место, где это
                    происходит. */}
                <AvatarFrame
                  frame={row.avatar_frame}
                  photoUrl={row.photo_url}
                  name={row.display_name}
                  size={28}
                />
                <span className="list__grow">{row.display_name}</span>
                <span className={`list__value num ${isTime ? 'value--time' : ''}`.trim()}>
                  {format(row.value)}
                </span>
              </div>
            ))}
          </div>

          {data.my_rank !== null && data.my_value !== null && (
            <MyRow data={data} isTime={isTime} format={format} playerName={playerName} />
          )}

          <span className="hint-text" style={{ textAlign: 'center' }}>
            {t('rating.refresh', {
              minutes: Math.round(data.refresh_seconds / 60),
              word: pluralOf('plural.minutes', Math.round(data.refresh_seconds / 60)),
            })}
          </span>

          {isTime && <span className="hint-text" style={{ textAlign: 'center' }}>{t('rating.timeNote')}</span>}
        </>
      )}
    </>
  )
}

function MyRow({
  data,
  isTime,
  format,
  playerName,
}: {
  data: LeaderboardResponse
  isTime: boolean
  format: (value: number) => string
  playerName: string
}) {
  // Что показать подписью: движение за неделю важнее отставания до
  // ступени — оно про уже случившееся, а не про гипотетическое.
  let note: string | null = null
  if (data.rank_delta) {
    const key = data.rank_delta > 0 ? 'rating.delta' : 'rating.deltaDown'
    const count = Math.abs(data.rank_delta)
    note = t(key, { count, word: pluralOf('plural.places', count) })
  } else if (data.milestone) {
    note = isTime
      ? t('rating.milestoneTime', {
          rank: data.milestone.rank,
          gap: formatGap(data.milestone.gap),
        })
      : t('rating.milestonePoints', {
          rank: data.milestone.rank,
          gap: formatNumber(data.milestone.gap),
        })
  }

  return (
    <div className="my-row">
      <Rank rank={data.my_rank ?? 0} />
      <span className="list__grow">
        <span style={{ fontWeight: 600, display: 'block' }}>
          {t('rating.you', { name: playerName })}
        </span>
        {note && <span className="my-row__note">{note}</span>}
      </span>
      <span className={`num ${isTime ? 'value--time' : ''}`.trim()} style={{ fontWeight: 600 }}>
        {format(data.my_value ?? 0)}
      </span>
    </div>
  )
}

function StatsTab() {
  const [stats, setStats] = useState<PlayerStats | null>(null)

  useEffect(() => {
    void fetchStats().then(setStats).catch(() => setStats(null))
  }, [])

  if (!stats) return <span className="hint-text">{t('app.loading')}</span>

  return (
    <>
      <div className="tiles">
        <Tile value={stats.games_played} label={t('stats.games')} />
        <Tile value={stats.games_won} label={t('stats.wins', { percent: stats.win_rate })} />
        <Tile value={stats.current_streak} label={t('stats.streak')} />
        <Tile value={stats.longest_streak} label={t('stats.bestStreak')} />
      </div>

      <div className="card card--accent">
        <div className="row">
          <span>
            <span style={{ fontWeight: 600, display: 'block' }}>{t('stats.place')}</span>
            <span style={{ fontSize: 'var(--size-hint)', opacity: 0.85 }}>
              {stats.rank === null
                ? t('stats.noPlace')
                : t('stats.ofPlayers', {
                    count: formatNumber(stats.total_players),
                    word: pluralOf('plural.players', stats.total_players),
                  })}
            </span>
          </span>
          <span className="tile__value num">{stats.rank ?? '—'}</span>
        </div>
      </div>

      <div className="stack stack--tight">
        <span className="section-label">{t('stats.bestTimes')}</span>
        <div className="list">
          {DIFFICULTIES.map((level, index) => {
            const best = stats.best_times?.[level]
            return (
              <div className="list__row list__row--compact" key={level}>
                <span
                  className="level-mark"
                  style={{ height: 16, background: `var(--level-${index + 1})` }}
                />
                <span className="list__grow">{t(`level.${level}`)}</span>
                {best ? (
                  <span className="list__value num">{formatTime(best)}</span>
                ) : (
                  <span className="hint-text muted-text">{t('stats.notSolved')}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

function Tile({ value, label }: { value: number; label: string }) {
  return (
    <div className="tile">
      <span className="tile__value num">{formatNumber(value)}</span>
      <span className="hint-text">{label}</span>
    </div>
  )
}

/**
 * Награды.
 *
 * Неполученные не прячутся и идут вперемешку с полученными, отсортированные
 * по величине награды: список читается как лестница, и следующая ступень
 * всегда видна. У накопительных условий вместо иконки счётчик — он
 * показывает, что цель достижима.
 */
function AwardsTab() {
  const [items, setItems] = useState<AchievementItem[] | null>(null)

  useEffect(() => {
    void fetchAchievements().then(setItems).catch(() => setItems([]))
  }, [])

  if (!items) return <span className="hint-text">{t('app.loading')}</span>

  const done = items.filter((item) => item.unlocked).length
  const earned = items
    .filter((item) => item.unlocked)
    .reduce((sum, item) => sum + item.reward, 0)

  return (
    <>
      <div className="row">
        <span className="hint-text">{t('awards.progress', { done, total: items.length })}</span>
        <span className="achievement__reward">
          <Crystal>{earned}</Crystal>
        </span>
      </div>
      <ProgressBar value={done} total={items.length} />

      <div className="stack">
        {items.map((item) => (
          <div
            key={item.code}
            className={`achievement ${item.unlocked ? 'achievement--done' : ''}`.trim()}
          >
            <span className={`badge ${item.unlocked ? 'badge--done' : ''}`.trim()}>
              {item.unlocked ? '✓' : item.target ? `${item.progress}/${item.target}` : '◇'}
            </span>
            <span className="list__grow">
              <span className="achievement__title" style={{ display: 'block' }}>
                {item.title}
              </span>
              <span className="hint-text">{item.description}</span>
            </span>
            <span className="achievement__reward">
              <Crystal>{item.reward}</Crystal>
            </span>
          </div>
        ))}
      </div>
    </>
  )
}
